import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BankIcon,
  CashSpecialIcon,
  WalletIcon,
} from "lola-framework-ui-test";
import { Popup } from "./Popup";
import { PayoutInfo } from "./PayoutInfo";
import {
  SelectPayoutMethodSlot,
  type FlatPayoutPick,
} from "./SelectPayoutMethodSlot";
import {
  PayoutDetailsSlot,
  type PayoutDetailsResult,
} from "./PayoutDetailsSlot";
import { PayoutMethodType } from "../interfaces/payout";
import { useComposeStore } from "../store/useComposeStore";
import { useRecipients } from "../hooks/useRecipients";
import { claimDefaultPayoutSelection } from "../utils/claimPayoutCompose";
import { bankAccountRowLabel } from "../utils/bankAccountLabel";
import type { IEstimateOption } from "../services/api";
import type { IUserData } from "../shared/session/interfaces/session";
import {
  claimRecipientDestinations,
  compatibleBookDestinations,
} from "../utils/recipient-destinations";

type PayoutStep = "method" | "details";

export interface FundRequestPayoutPopupsProps {
  visible: boolean;
  onClose: () => void;
  destinationCountry: string;
  destinationCurrency: string;
  sourceCountry: string;
  fundingMethod: string | null;
  options: IEstimateOption[];
  optionsLoading: boolean;
  availableMethods: PayoutMethodType[];
  userData: IUserData | undefined;
  onSelectionChange: () => void;
}

export function FundRequestPayoutPopups({
  visible,
  onClose,
  destinationCountry,
  destinationCurrency,
  sourceCountry,
  fundingMethod,
  options,
  optionsLoading,
  availableMethods,
  userData,
  onSelectionChange,
}: FundRequestPayoutPopupsProps) {
  const { t } = useTranslation();
  const selection = useComposeStore((s) => s.selection);
  const setSelection = useComposeStore((s) => s.setSelection);
  const { recipients } = useRecipients();

  const [step, setStep] = useState<PayoutStep>("method");
  const [pendingMethod, setPendingMethod] = useState<PayoutMethodType | null>(
    null,
  );

  const defaultSelection = useMemo(
    () => claimDefaultPayoutSelection(userData),
    [userData],
  );

  // Only habits for this requester in the claim's pinned country. The same
  // person in MX and AR has separate recipient-book identities/instruments.
  const allDestinations = useMemo(
    () =>
      claimRecipientDestinations(recipients, userData, destinationCountry),
    [destinationCountry, recipients, userData],
  );

  const cardBookEntries = useMemo(
    () =>
      compatibleBookDestinations(
        allDestinations,
        PayoutMethodType.PUSH_TO_CARD,
        options,
      ),
    [allDestinations, options],
  );
  const bankBookEntries = useMemo(
    () =>
      compatibleBookDestinations(
        allDestinations,
        PayoutMethodType.BANK_ACCOUNT,
        options,
      ),
    [allDestinations, options],
  );

  const applyDefaultRow = useCallback(() => {
    if (!defaultSelection) return;
    setSelection(defaultSelection);
    onSelectionChange();
    onClose();
  }, [defaultSelection, onClose, onSelectionChange, setSelection]);

  const defaultRow = useMemo(() => {
    if (!defaultSelection) return null;
    const pm = userData?.paymentMethod;
    const last4 = (value?: string) => value?.slice(-4);

    let title = t(`payout.methods.${defaultSelection.method}`);
    let icon = <WalletIcon size={22} />;

    if (pm?.type === "cash") {
      const payer = pm.cash?.payer ?? pm.cash?.provider;
      title = payer
        ? t("transfer.cashPickup", { payer })
        : t("payout.methods.CASH_PICKUP");
      icon = <CashSpecialIcon size={22} />;
    } else if (pm?.type === "bank_account") {
      const digits = last4(pm.bankAccount?.accountNumber);
      title = bankAccountRowLabel({
        stored: pm.bankAccount?.bankName,
        accountOrLast4: pm.bankAccount?.accountNumber,
        unnamedWithDigits: t("transfer.bankAccount", { last4: digits }),
        empty: pm.bankAccount?.bankName ?? t("payout.methods.BANK_ACCOUNT"),
      });
      icon = <BankIcon size={22} />;
    } else if (pm?.type === "card") {
      const digits = last4(pm.card?.last4);
      title = digits
        ? t("transfer.cardEnding", { last4: digits })
        : t("payout.methods.PUSH_TO_CARD");
    }

    return (
      <PayoutInfo
        title={title}
        subtitle={t("payout.default")}
        icon={icon}
        className="funding-neutral-icon"
        onChange={applyDefaultRow}
      />
    );
  }, [applyDefaultRow, defaultSelection, t, userData?.paymentMethod]);

  const handlePayoutPick = useCallback(
    (pick: FlatPayoutPick) => {
      if (pick.kind === "method") {
        setPendingMethod(pick.method);
        setStep("details");
        return;
      }
      if (pick.kind === "card" || pick.kind === "bank") {
        const entry = pick.entry;
        const method =
          pick.kind === "card"
            ? PayoutMethodType.PUSH_TO_CARD
            : PayoutMethodType.BANK_ACCOUNT;
        setSelection({
          method,
          option: null,
          payout: entry.payout,
          origin: "user",
        });
        onSelectionChange();
        onClose();
        return;
      }
      if (pick.kind === "add") {
        setPendingMethod(PayoutMethodType.PUSH_TO_CARD);
        setStep("details");
        return;
      }
      if (pick.kind === "addBank") {
        setPendingMethod(PayoutMethodType.BANK_ACCOUNT);
        setStep("details");
      }
    },
    [onClose, onSelectionChange, setSelection],
  );

  const handleDetailsSubmit = useCallback(
    (result: PayoutDetailsResult) => {
      if (!pendingMethod) return;
      setSelection({
        method: pendingMethod,
        option: result.option,
        payout: result.payout,
        origin: "user",
      });
      onSelectionChange();
      onClose();
      setStep("method");
      setPendingMethod(null);
    },
    [onClose, onSelectionChange, pendingMethod, setSelection],
  );

  if (!visible) return null;

  return (
    <Popup
      visible
      onClose={() => {
        setStep("method");
        setPendingMethod(null);
        onClose();
      }}
    >
      <section className="fund-request-payout-popup">
        {step === "method" ? (
            <SelectPayoutMethodSlot
              selectedMethod={selection?.method ?? null}
              selectedPaymentMethodId={
                selection?.method === PayoutMethodType.PUSH_TO_CARD ||
                selection?.method === PayoutMethodType.BANK_ACCOUNT
                  ? (selection.payout.paymentMethodId ?? null)
                  : null
              }
              available={availableMethods}
              cardEntries={cardBookEntries}
              bankEntries={bankBookEntries}
              loading={optionsLoading}
              prependRows={defaultRow}
              onPick={handlePayoutPick}
              onClose={onClose}
            />
        ) : pendingMethod ? (
          <PayoutDetailsSlot
            method={pendingMethod}
            destinationCountry={destinationCountry}
            destinationCurrency={destinationCurrency}
            fundingMethod={fundingMethod}
            originCountryCode={sourceCountry}
            options={options}
            optionsLoading={optionsLoading}
            bookDestinations={allDestinations}
            value={
              // The requester's snapshot already has its own row in the picker;
              // pre-filling the form with it would fight the payer's edit.
              selection &&
              selection.origin === "user" &&
              selection.method === pendingMethod
                ? selection.payout
                : null
            }
            onBack={() => setStep("method")}
            onClose={onClose}
            onSubmit={handleDetailsSubmit}
          />
        ) : (
          <span />
        )}
      </section>
    </Popup>
  );
}
